import { Injectable } from '@angular/core';
import { containerAdaptor, resourceAdaptor } from 'qp-adaptor';
import {
  StorefrontService as Service,
  createStorefrontService,
} from 'qp-storefront';
import {
  Container,
  ContainerList,
  ContainerVm,
  Resource,
  ResourceList,
  ResourceVm,
  StorefrontVm,
} from 'qp-ui-interface';
import { PageType } from '../analytics/newrelic/enums/PageType';
import { ErrorFactory } from '../shared/modules/errors/handler/ErrorFactory';
import { ConfigService } from '../shared/services/config.service';
import { LocalStorageService } from '../shared/services/storage/local-storage.service';

import { StorefrontPolicyService } from '../shared/services/firstlight/storefront-policy/storefront-policy.service';
import { ErrorEvents } from '../shared/utils/ErrorUtils';
import { getDeviceType } from '../shared/utils/deviceUtils';
import { AdaptorData } from './AdaptorData';
import { KEYS } from '../shared/utils/constants/storage.constants';
import { MetaDataService } from '../shared/services/meta-data.service';
import { NewrelicService } from '../analytics/newrelic/services/newrelic.service';

@Injectable({
  providedIn: 'root',
})
export class StorefrontService {
  private storefrontService: Service;

  private storefrontContentLimit;

  constructor(
    private readonly configService: ConfigService,
    private readonly policyService: StorefrontPolicyService,
    private readonly localStorage: LocalStorageService,
    private readonly metadataService: MetaDataService,
    private readonly newrelicService: NewrelicService
  ) {}

  initialiseServices(): void {
    this.storefrontService = createStorefrontService(
      this.configService.applicationConfig.storefrontURL,
      this.configService.applicationConfig.catalogURL,
      '',
      this.policyService.getPolicy()
    );

    if (this.configService.applicationConfig.storefrontContentLimit) {
      this.storefrontContentLimit =
        this.configService.applicationConfig.storefrontContentLimit;
    } else {
      this.storefrontContentLimit = 100;
    }
  }

  public async fetchResourceDetails(
    adaptorData: AdaptorData
  ): Promise<ResourceVm> {
    try {
      const resource: Resource = await this.metadataService.getResourceDetails(
        adaptorData.resourceType,
        adaptorData.resourceId
      );
      return resourceAdaptor(
        this.configService.applicationConfig.imageResizeURL,
        resource as unknown as Resource,
        getDeviceType(),
        '0-16x9',
        adaptorData.container,
        undefined,
        {
          storefrontId: adaptorData?.storefrontId,
          tabId: adaptorData?.tabId,
          tabName: adaptorData?.tabName,
          originScreen: adaptorData?.originScreen,
          collectionId: adaptorData?.collectionId,
          collectionTitle: adaptorData?.collectionTitle,
          containerName: adaptorData?.containerName,
          containerId: adaptorData?.containerId,
        }
      );
    } catch (error) {
      this.newrelicService.sendErrorEvent({
        error,
      });
      return Promise.reject(
        ErrorFactory.get(error, ErrorEvents.RESOURCE_FETCH_ERROR)
      );
    }
  }

  public async fetchCollections(
    collectionId: string,
    pageNumber: string = '1',
    pageSize: string = '20',
    tabId?: string,
    tabName?: string,
    originScreen?: string,
    collectionTitle?: string
  ): Promise<ContainerVm[]> {
    try {
      let containers: Container[] = await this.storefrontService.getCollections(
        collectionId,
        pageNumber,
        pageSize
      );
      containers = containers.map((it) => {
        it.iar = '0-2x3';
        return it;
      });
      return containers.map((container: Container) =>
        containerAdaptor(
          this.configService.applicationConfig.imageResizeURL,
          container,
          getDeviceType(),
          undefined,
          {
            tabId,
            tabName,
            originScreen,
            collectionId,
            collectionTitle,
          }
        )
      );
    } catch (error) {
      this.newrelicService.sendErrorEvent({
        error,
      });
      return Promise.reject(
        ErrorFactory.get(error, ErrorEvents.COLLECTION_FETCH_ERROR)
      );
    }
  }

  public async fetchContainers(
    tabId: string,
    url = this.configService.applicationConfig.catalogURL,
    storefrontId = this.localStorage.getItem(KEYS.STOREFRONT_ID),
    tabName?: string,
    originScreen?: string,
    pageNumber?: string,
    pageSize?: string
  ): Promise<StorefrontVm> {
    try {
      const storefrontPath = 'catalog/storefront';
      const containersResponse: ContainerList =
        await this.storefrontService.getContainers(
          storefrontId || '',
          tabId,
          storefrontPath,
          url,
          false,
          pageNumber,
          pageSize
        );

      const containers = containersResponse.containers;

      return {
        containers: containers.map((container) => {
          const containerNameLanguage = container.lon.find(
            (l) => l.lang === 'en'
          );
          const containerName =
            containerNameLanguage?.n.toLowerCase() === 'highlights'
              ? { n: 'Hero' }
              : containerNameLanguage;
          const containerId = container.id;
          return containerAdaptor(
            this.configService.applicationConfig.imageResizeURL,
            container,
            getDeviceType(),
            undefined,
            {
              storefrontId: storefrontId || '',
              tabId,
              tabName,
              originScreen,
              containerName: containerName?.n,
              containerId,
            },
            Number(this.storefrontContentLimit)
          );
        }),
        totalContainers: containersResponse.totalContainers,
      };
    } catch (error) {
      this.newrelicService.sendErrorEvent({
        error,
      });
      return Promise.reject(
        ErrorFactory.get(error, ErrorEvents.CONTAINER_FETCH_ERROR)
      );
    }
  }

  async fetchUpNextContent(
    id: string,
    genres: { en: string[]; ta: string[]; te: string[] },
    contentType: string,
    providerName: string,
    seasonNum?: number,
    episodeNumber?: number
  ): Promise<ResourceVm> {
    try {
      const parameters = {
        g: genres['en'].join(','),
        cty: contentType,
        id,
        pageSize: '1',
        pn: providerName,
        snum: seasonNum,
        epnum: episodeNumber,
      };
      const response =
        await this.metadataService.fetchUpNextContent(parameters);
      if (!response) {
        return null;
      }
      const data = resourceAdaptor(
        this.configService.applicationConfig.imageResizeURL,
        response && response?.[0],
        getDeviceType(),
        '0-16x9',
        undefined,
        undefined,
        {
          collectionTitle: PageType.RelatedResource,
        }
      );
      // Replace width=300 with width=553 in the image URL
      if (data.image) {
        data.image = data.image.replace(/width=300/g, 'width=553');
      }
      return data;
    } catch (error) {
      this.newrelicService.sendErrorEvent({
        error,
      });
      return null;
    }
  }

  public async fetchViewMore(
    viewMoreUrl: string[],
    container: ContainerVm,
    pageNumber: number = 1,
    pageSize: number = 20,
    customData?: {
      tabId: string;
      tabName: string;
      originScreen?: string;
      collectionTitle?: string;
    }
  ): Promise<ContainerVm[]> {
    try {
      if (!customData) {
        customData = {
          tabId: '',
          tabName: '',
          originScreen: '',
          collectionTitle: '',
        };
      }
      const { tabId, tabName, originScreen, collectionTitle } = customData;
      const result: ResourceList =
        await this.metadataService.fetchViewMoreContainer(
          viewMoreUrl,
          pageNumber,
          pageSize
        );
      if (!result) {
        return null;
      }
      const allResources = result.resources.map((resource) =>
        resourceAdaptor(
          this.configService.applicationConfig.imageResizeURL,
          resource,
          getDeviceType(),
          '0-2x3',
          undefined,
          undefined,
          {
            tabId,
            tabName,
            originScreen,
            collectionTitle: container.name,
            collectionId: container.id,
          }
        )
      );
      if (pageNumber !== 1) {
        container.resources?.push(...allResources);
      } else {
        container.resources = allResources;
      }
      return [container];
    } catch (error) {
      this.newrelicService.sendErrorEvent({
        error,
      });
    }
    return null;
  }

  public async fetchCollection(
    container: ContainerVm,
    viewMoreUrl: string[],
    pageNumber: number = 1,
    pageSize: number = 20
  ): Promise<ContainerVm[] | null> {
    if (!viewMoreUrl?.length) {
      return null;
    }
    const urlsToFetch = viewMoreUrl[0];
    try {
      // Create container copy once
      const containerCopy = {
        ...container,
        resources: [],
        contentCount: 0,
        viewMoreUrl: [urlsToFetch],
      };

      const fetchPromises = [urlsToFetch].map(async (url) => {
        try {
          if (url.includes('query=')) {
            return await this.metadataService.fetchViewMore(
              [url],
              pageNumber,
              pageSize,
              containerCopy
            );
          } else {
            const result = await this.fetchViewMore(
              [url],
              containerCopy,
              pageNumber,
              pageSize
            );
            return result?.[0] || null;
          }
        } catch (error) {
          console.warn(`Failed to fetch data for URL: ${url}`, error);
          return null;
        }
      });

      // Wait for all promises to resolve
      const results = await Promise.all(fetchPromises);

      // Filter out null results and aggregate data
      const validResults: any = results.filter((result) => result !== null);

      if (!validResults.length) {
        return null;
      }

      // Aggregate resources and content count
      const allResources = validResults.flatMap(
        (result) => result.resources || []
      );
      const totalContentCount = validResults.reduce(
        (sum, result) => sum + (result.contentCount || 0),
        0
      );

      // Update container with aggregated data
      allResources.forEach((it) => {
        ((it.collectionID = containerCopy.id),
          (it.collectionName = containerCopy.name));
      });
      containerCopy.resources = allResources;
      containerCopy.contentCount = totalContentCount;

      return [containerCopy];
    } catch (error) {
      this.newrelicService.sendErrorEvent({
        error,
      });
      return null;
    }
  }
}
